import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  onZopimConnected,
  setAgentInfo,
  setLiveChatStatus,
  setQueuePosition,
} from 'reducers/live-chat';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectConversationIdUsedInPreviousSession,
  selectFeatureFlags,
  selectForethoughtLiveChatWidgetComponent,
  setCsatStatesByKey,
} from 'reducers/workflows';
import type {
  ButtonComponent,
  FileAttachment,
  UserMessageComponent,
} from 'api/types';
import type {
  ZopimChatFileEventData,
  ZopimChatMsgEventData,
  ZopimChatRequestRatingEventData,
  ZopimConnectionStatus,
  ZopimError,
  ZopimEventData,
  ZopimMemberJoinEventData,
  ZopimMemberLeaveEventData,
  ZopimQueuePositionEventData,
  ZopimTypingEventData,
} from 'embed/zopim';
import {
  debouncedSetupZopimSdk,
  zopimDidAgentEndChat,
  initZopimChat,
  ZOPIM_SCRIPT_ID,
  applyZopimFunction,
  ZOPIM_WEB_SDK_URL,
} from 'embed/zopim';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import {
  addLiveChatEventMessage,
  addLiveChatWidgetComponents,
  concludeLiveChat,
  handleAgentTyping,
  handleZopimChatId,
  sendUserReply,
} from 'actions/live-chat';
import { selectWidgetInitMethod } from 'reducers/config';
import { postMessage } from 'utils/postMessage';
import type {
  ForethoughtEvent,
  ForethoughtZopimCallbackEvent,
} from 'embed/embed';
import { useLiveChatFailure } from './useLiveChatFailure';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { TWO_MINUTES_MS } from 'embed/constants';

let interval: NodeJS.Timeout;

/**
 * In order for the zopim web SDK persistence to work correctly,
 * the SDK needs to be initialized on the parent page and
 * so we are using message events to send data to the parent
 * page and back. Code for that is found in `zopim.ts`.
 *
 * On our url (solve-widget.forethought.ai) we do not need to
 * do that.
 */
export const useHandleZendeskChatInitialization = () => {
  const dispatch = useAppDispatch();
  const handleLiveChatFailure = useLiveChatFailure();
  const conversationId = useAppSelector(selectConversationId);
  const liveChatWidgetComponent = useAppSelector(
    selectForethoughtLiveChatWidgetComponent,
  );
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const conversationIdUsedInPreviousSession = useAppSelector(
    selectConversationIdUsedInPreviousSession,
  );
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isPatchedSdkEnabled = featureFlags.includes(
    'forethought_hosted_zopim_sdk',
  );

  const getAvatarUrl = (nick: string) => {
    let avatarUrl = '';
    const agents = window.zChat?.getServingAgentsInfo() || [];

    for (const agent of agents) {
      if (agent.nick === nick) {
        avatarUrl = agent.avatar_path;
        break;
      }
    }

    return avatarUrl ? avatarUrl : '';
  };

  const onChatEnded = React.useCallback(
    (e: ZopimMemberLeaveEventData) => {
      dispatch(
        addLiveChatEventMessage({
          message: 'Chat ended',
          timestamp: e.timestamp,
        }),
      );
      dispatch(concludeLiveChat());
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-msg-chat-event-type
   */
  const chatMsgHandler = React.useCallback(
    (e: ZopimChatMsgEventData) => {
      // text message
      if (e.msg) {
        dispatch(
          addLiveChatWidgetComponents({
            components: [
              {
                component_fields: {
                  agentIcon: getAvatarUrl(e.nick),
                  agentName: e.display_name,
                  author: e.display_name,
                  message: e.msg,
                  translated_message: e.translation?.msg$string,
                },
                component_type: 'text_message',
                hideTimestamp: true,
                isInAgentConvo: true,
                isSent: true,
                timestamp: e.timestamp,
              } satisfies UserMessageComponent,
            ],
            sendAgentMessage: true,
            status: 'active',
          }),
        );

        if (e.chat_id) {
          dispatch(handleZopimChatId(e.chat_id));
        }
      }

      // options - no need to sendAgentMessage
      if (e.options.length) {
        dispatch(
          addLiveChatWidgetComponents({
            components: [
              {
                component_fields: {
                  multi_select: false,
                  options: e.options.map(option => ({
                    display_name: option,
                    value: option,
                  })),
                  output_variable: '',
                  prompt: '',
                },
                component_type: 'buttons',
                timestamp: e.timestamp,
              } satisfies ButtonComponent,
            ],
          }),
        );
      }

      // structured message
      if (e.structured_msg) {
        if (e.structured_msg.type === 'QUICK_REPLIES') {
          dispatch(
            addLiveChatWidgetComponents({
              components: [
                {
                  component_fields: {
                    multi_select: false,
                    options: e.structured_msg.quick_replies.map(quickReply => ({
                      display_name: quickReply.text,
                      value: quickReply.action.value,
                    })),
                    output_variable: '',
                    prompt: e.structured_msg.msg,
                  },
                  component_type: 'buttons',
                  timestamp: e.timestamp,
                } satisfies ButtonComponent,
              ],
              sendAgentMessage: true,
            }),
          );
        }

        if (e.structured_msg.type === 'BUTTON_TEMPLATE') {
          dispatch(
            addLiveChatWidgetComponents({
              components: [
                {
                  component_fields: {
                    multi_select: false,
                    options: e.structured_msg.buttons.map(button => ({
                      display_name: button.text,
                      url:
                        button.action.type === 'LINK_ACTION'
                          ? button.action.value
                          : undefined,
                      value: button.action.value,
                    })),
                    output_variable: '',
                    prompt: e.structured_msg.msg,
                  },
                  component_type: 'buttons',
                  timestamp: e.timestamp,
                } satisfies ButtonComponent,
              ],
              sendAgentMessage: true,
            }),
          );
        }

        // TODO: support for Panel Template, Panel Template Carousel, List Template
      }
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-file-chat-event-type
   */
  const chatFileHandler = React.useCallback(
    (e: ZopimChatFileEventData) => {
      const attachment: FileAttachment = {
        content_type: e.attachment.mime_type,
        file_extension: e.attachment.name.split('.').pop(),
        filesize: e.attachment.size,
        name: e.attachment.name,
        url: e.attachment.url,
      };
      dispatch(
        addLiveChatWidgetComponents({
          components: [
            {
              component_fields: {
                agentIcon: getAvatarUrl(e.nick),
                agentName: e.display_name,
                attachments: [attachment],
                author: e.display_name,
                message: '',
              } satisfies UserMessageComponent['component_fields'],
              component_type: 'text_message',
              hideTimestamp: true,
              isInAgentConvo: true,
              isSent: true,
              timestamp: e.timestamp,
            },
          ],
          sendAgentMessage: true,
        }),
      );
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-memberjoin-chat-event-type
   */
  const chatMemberJoinHandler = React.useCallback(
    (e: ZopimMemberJoinEventData) => {
      dispatch(
        setAgentInfo({
          agentIcon: getAvatarUrl(e.nick),
          agentName: e.display_name,
        }),
      );
      dispatch(
        addLiveChatEventMessage({
          message: `${e.display_name} has joined the chat`,
          status: 'active',
          timestamp: e.timestamp,
        }),
      );
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-memberleave-chat-event-type
   */
  const chatMemberLeaveHandler = React.useCallback(
    (e: ZopimMemberLeaveEventData) => {
      dispatch(
        addLiveChatEventMessage({
          message: `${e.display_name} has left the chat`,
          timestamp: e.timestamp,
        }),
      );

      /**
       * Needs to be in a setTimeout or else isChatting will still be true
       *
       * When loading via script, zopim.ts will post forethoughtZopimChatEnded
       * to the iframe which will call onChatEnded.
       */
      if (widgetInitMethod === 'url') {
        setTimeout(() => {
          if (zopimDidAgentEndChat(e)) {
            onChatEnded(e);
          }
        });
      }
    },
    [dispatch, onChatEnded, widgetInitMethod],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-queue_position-chat-event-type
   */
  const chatQueuePositionHandler = React.useCallback(
    (e: ZopimQueuePositionEventData) => {
      dispatch(setQueuePosition(e.queue_position));
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#typing-event
   */
  const chatTypingHandler = React.useCallback(
    (e: ZopimTypingEventData) => {
      dispatch(handleAgentTyping({ isTyping: e.typing }));
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-request-rating-chat-event-type
   */
  const chatRequestRatingHandler = React.useCallback(
    (_: ZopimChatRequestRatingEventData) => {
      dispatch(
        setCsatStatesByKey({
          isCsatVisible: true,
          triggeredFrom: 'zopim-chat-rating-request',
        }),
      );
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#chat-2
   */
  const chatHandler = React.useCallback(
    (e: ZopimEventData<'chat'>) => {
      // We are only handling incoming chat events from the agent
      // chat.queue_position will always have nick system.queue
      if (!e.nick.startsWith('agent:') && e.nick !== 'system.queue') {
        return;
      }

      switch (e.type) {
        case 'chat.msg':
          chatMsgHandler(e);
          break;
        case 'chat.file':
          chatFileHandler(e);
          break;
        case 'chat.memberjoin':
          chatMemberJoinHandler(e);
          break;
        case 'chat.memberleave':
          chatMemberLeaveHandler(e);
          break;
        case 'chat.queue_position':
          chatQueuePositionHandler(e);
          break;
        case 'typing':
          chatTypingHandler(e);
          break;
        case 'chat.request.rating':
          chatRequestRatingHandler(e);
      }
    },
    [
      chatMsgHandler,
      chatFileHandler,
      chatMemberJoinHandler,
      chatMemberLeaveHandler,
      chatQueuePositionHandler,
      chatTypingHandler,
      chatRequestRatingHandler,
    ],
  );

  /**
   * https://api.zopim.com/web-sdk/#39-agent_update-39-event
   */
  const agentUpdateHandler = React.useCallback(
    (e: ZopimEventData<'agent_update'>) => {
      dispatch(
        setAgentInfo({
          agentIcon: e.avatar_path,
          agentName: e.display_name,
        }),
      );
    },
    [dispatch],
  );

  /**
   * https://api.zopim.com/web-sdk/#39-connection_update-39-event
   */
  const connectionUpdateHandler = React.useCallback(
    (status: ZopimConnectionStatus) => {
      switch (status) {
        case 'closed':
          dispatch(setLiveChatStatus('inactive'));
          applyZopimFunction(widgetInitMethod, 'reconnect');
          break;
        // useHandleLiveChatSetup sets liveChatStatus to 'pending'
        // chatMsgHandler sets liveChatStatus to 'active'
        case 'connecting':
        case 'connected':
          // only set to active if inactive
          // page refreshes can cause this event to fire a lot
          dispatch(onZopimConnected());
          break;
      }
    },
    [dispatch, widgetInitMethod],
  );

  /**
   * https://api.zopim.com/web-sdk/#39-error-39-event
   */
  const errorHandler = React.useCallback(
    (err: ZopimError) => {
      console.error(`Forethought + Zopim SDK Error: ${err}`);

      // send zChat.init errors to Sentry
      if (err.message.includes('Zendesk Chat Web SDK: Error: init:')) {
        sendErrorToSentry({
          conversationId: conversationId,
          error: err,
        });
      }
    },
    [conversationId],
  );

  /**
   * callback handler
   *
   * because the zopim one chat can run on the parent page, we need to use
   * postMessage to send messages back and forth from our application. This is
   * the handler for all zopim functions with callbacks and can be used to handle their
   * successes or failures.
   */
  const callbackHandler = React.useCallback(
    (e: ForethoughtZopimCallbackEvent['data']) => {
      try {
        switch (e.function) {
          case 'sendChatMsg':
            if (e.error) {
              const errorMessage = 'Message failed to send';
              dispatch(addScreenReaderNotification(errorMessage));
              dispatch(addLiveChatEventMessage({ message: errorMessage }));
            } else {
              const message = e.args[0] as string;
              // we don't want to show transcript messages
              if (
                message &&
                !message.includes('[Chatbot]:') &&
                !message.includes('[user]:')
              ) {
                dispatch(sendUserReply({ message }));
              }
            }
            break;
          case 'sendFile':
            if (e.error) {
              const errorMessage = 'File failed to send';
              dispatch(addScreenReaderNotification(errorMessage));
              dispatch(addLiveChatEventMessage({ message: errorMessage }));
            } else {
              const file = e.args[0] as File;
              const attachment: FileAttachment = {
                content_type: file.type,
                file_extension: file.name.split('.').pop(),
                filesize: file.size,
                name: file.name,
                url: '',
              };
              dispatch(
                sendUserReply({ attachments: [attachment], message: '' }),
              );
            }
            break;
        }
      } catch (err) {
        sendErrorToSentry({
          conversationId: conversationId,
          error: err,
        });
      }
    },
    [conversationId, dispatch],
  );

  /**
   * parent page message handler
   *
   * IMPORTANT: keep up to date with zopim.ts listeners
   */
  const messageHandler = React.useCallback(
    (e: ForethoughtEvent) => {
      switch (e.data.event) {
        case 'forethoughtZopimAgentUpdate':
          agentUpdateHandler(e.data.zopimData);
          break;
        case 'forethoughtZopimChat':
          chatHandler(e.data.zopimData);
          break;
        case 'forethoughtZopimConnectionUpdate':
          connectionUpdateHandler(e.data.zopimData);
          break;
        case 'forethoughtZopimPending':
          dispatch(setLiveChatStatus('pending'));
          break;
        case 'forethoughtZopimFailure':
          handleLiveChatFailure({
            error: e.data.error,
            integration: 'zendesk-zopim-live-chat',
          });
          break;
        case 'forethoughtZopimError':
          errorHandler(e.data.error);
          break;
        case 'forethoughtZopimCallback':
          callbackHandler(e.data);
          break;
        case 'forethoughtZopimChatEnded':
          onChatEnded(e.data.zopimData);
          break;
      }
    },
    [
      agentUpdateHandler,
      callbackHandler,
      chatHandler,
      connectionUpdateHandler,
      dispatch,
      errorHandler,
      handleLiveChatFailure,
      onChatEnded,
    ],
  );

  /**
   * https://api.zopim.com/web-sdk/#zchat-un-event_name-handler
   */
  const removeListeners = React.useCallback(() => {
    window.zChat?.un('agent_update', agentUpdateHandler);
    window.zChat?.un('chat', chatHandler);
    window.zChat?.un('connection_update', connectionUpdateHandler);
    window.zChat?.un('error', errorHandler);
    window.removeEventListener('message', messageHandler);
    if (interval) {
      clearInterval(interval);
    }
  }, [
    agentUpdateHandler,
    chatHandler,
    connectionUpdateHandler,
    errorHandler,
    messageHandler,
  ]);

  /**
   * https://api.zopim.com/web-sdk/#zchat-on-event_name-handler
   */
  const setupListeners = React.useCallback(() => {
    window.zChat?.on('agent_update', agentUpdateHandler);
    window.zChat?.on('chat', chatHandler);
    window.zChat?.on('connection_update', connectionUpdateHandler);
    window.zChat?.on('error', errorHandler);
    window.addEventListener('message', messageHandler);

    const chatLog = window.zChat?.getChatLog() || [];
    chatLog.forEach(chat => {
      // ignore rating requests here
      if (chat.type === 'chat.request.rating') return;

      chatHandler(chat);
    });

    interval = setInterval(() => {
      window.zChat?.markAsRead();
    }, TWO_MINUTES_MS);
  }, [
    agentUpdateHandler,
    chatHandler,
    connectionUpdateHandler,
    errorHandler,
    messageHandler,
  ]);

  React.useEffect(() => {
    if (
      isCurrentTabPrimary &&
      liveChatWidgetComponent &&
      liveChatWidgetComponent.component_fields.integration_config
        .integration === 'zendesk'
    ) {
      const integrationFields =
        liveChatWidgetComponent.component_fields.integration_config
          .integration_fields;

      if (widgetInitMethod === 'script') {
        setupListeners();
        postMessage({
          conversationId: conversationId,
          conversationIdUsedInPreviousSession:
            conversationIdUsedInPreviousSession,
          event: 'forethoughtZopimInit',
          forethoughtHosted: isPatchedSdkEnabled,
          integrationFields: integrationFields,
          liveChatWidgetComponent: liveChatWidgetComponent,
        });
      } else if (window.zChat) {
        debouncedSetupZopimSdk({
          accountKey: integrationFields.account_key,
          postInit: () =>
            initZopimChat({
              conversationId,
              conversationIdUsedInPreviousSession,
              integrationFields,
              liveChatWidgetComponent,
            }),
          setupListeners: setupListeners,
        });
      } else {
        const script = document.createElement('script');
        /**
         * Because of CSP issues that customers have we need this behind a feature flag
         */
        const scriptUrl = isPatchedSdkEnabled
          ? `${window.location.origin}/vendor/zendesk/zopim-web-sdk.js`
          : ZOPIM_WEB_SDK_URL;

        script.id = ZOPIM_SCRIPT_ID;
        script.src = scriptUrl;
        script.fetchPriority = 'high';
        script.onload = () => {
          debouncedSetupZopimSdk({
            accountKey: integrationFields.account_key,
            postInit: () =>
              initZopimChat({
                conversationId,
                conversationIdUsedInPreviousSession,
                integrationFields,
                liveChatWidgetComponent,
              }),
            setupListeners: setupListeners,
          });
        };
        document.body.appendChild(script);
      }
    }

    return () => {
      removeListeners();

      if (widgetInitMethod === 'script') {
        postMessage({ event: 'forethoughtZopimRemove' }, '*');
      } else {
        document.getElementById(ZOPIM_SCRIPT_ID)?.remove();
      }
    };
  }, [
    liveChatWidgetComponent,
    conversationId,
    setupListeners,
    removeListeners,
    isCurrentTabPrimary,
    conversationIdUsedInPreviousSession,
    dispatch,
    widgetInitMethod,
    isPatchedSdkEnabled,
  ]);
};
